import React, { useEffect, useMemo, useRef, useState } from 'react';
import { displayClientName, clientNameMatches } from '../clientNameUtils';

const MAX_RESULTS = 25;

// Searchable client picker: type to filter (matches "First Last" and
// "Last, First" forms, words in any order), arrows + Enter or click to
// choose. Selected state renders as a filled box with a clear (✕) button.
export default function ClientTypeAhead({ clients, selectedId, onSelect, inputStyle }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef(null);

  const selected = useMemo(
    () => clients.find((c) => String(c.id) === String(selectedId)) || null,
    [clients, selectedId]
  );

  const matches = useMemo(() => {
    const sorted = [...clients].sort((a, b) =>
      displayClientName(a.client_name).localeCompare(displayClientName(b.client_name))
    );
    return sorted.filter((c) => clientNameMatches(c.client_name, query)).slice(0, MAX_RESULTS);
  }, [clients, query]);

  useEffect(() => { setHighlight(0); }, [query, open]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const label = (c) => `${displayClientName(c.client_name)}${c.is_active === false ? ' (inactive)' : ''}`;

  const choose = (c) => {
    onSelect(String(c.id));
    setQuery('');
    setOpen(false);
  };

  const clear = () => {
    onSelect('');
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      else setHighlight((h) => Math.min(h + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && matches[highlight]) choose(matches[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  if (selected) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div style={{ ...inputStyle, marginBottom: 0, background: 'var(--surface-muted)' }}>
          {label(selected)}
        </div>
        <button
          type="button"
          onClick={clear}
          aria-label="Clear client"
          style={{
            border: '1px solid var(--border-color)', borderRadius: 6,
            background: 'var(--white)', cursor: 'pointer', padding: '6px 10px',
          }}
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <input
        style={inputStyle}
        value={query}
        placeholder="Type a client name…"
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        aria-label="Client search"
      />
      {open && (
        <div
          style={{
            position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0,
            maxHeight: 240, overflowY: 'auto', background: 'var(--white)',
            border: '1px solid var(--border-color)', borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)', marginTop: -6,
          }}
        >
          {matches.length === 0 ? (
            <div style={{ padding: 10, fontSize: 13, color: 'var(--text-muted)' }}>No matching clients</div>
          ) : matches.map((c, i) => (
            <div
              key={c.id}
              onMouseDown={(e) => { e.preventDefault(); choose(c); }}
              onMouseEnter={() => setHighlight(i)}
              style={{
                padding: '8px 10px', fontSize: 14, cursor: 'pointer',
                background: i === highlight ? 'var(--surface-muted)' : 'var(--white)',
                color: c.is_active === false ? 'var(--text-muted)' : 'inherit',
              }}
            >
              {label(c)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
